import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES, PROJECTS } from "@/lib/site";

const FEATURES = [
  "Made-to-measure custom kitchen cabinets designed for your exact space",
  "Complete kitchen renovations from design through installation",
  "Cabinet refacing to refresh your existing kitchen without full replacement",
  "Smart storage: pull-outs, organizers, pantry systems and more",
  "Quality materials, hardware, and finishes built for daily life",
  "Professional, clean installation across the GTA",
];

const kitchenProjects = PROJECTS.filter((p) => ["Kitchens", "Renovations"].includes(p.cat));

export default function Kitchens() {
  return (
    <main data-testid="kitchens-page">
      <SEO
        title="Custom Kitchen Cabinets & Kitchen Renovations Toronto & GTA | Hive Interiors"
        description="Custom kitchen cabinets, complete kitchen renovations and cabinet refacing in Toronto, Scarborough and across the GTA. Modern design, smart storage, quality craftsmanship."
      />
      <PageHero
        image={IMAGES.kitchenHero}
        overline="Kitchens"
        title="Custom Kitchen Cabinets"
        italic="& Complete Renovations"
        sub="The kitchen is the heart of your home. We design and build made-to-measure cabinetry and complete kitchen transformations around how you actually live."
        alt="Custom kitchen cabinets in a renovated Toronto kitchen"
      />

      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <Overline>01 / Custom Cabinetry</Overline>
              <h2 className="mt-5 font-heading text-4xl md:text-5xl tracking-tight leading-[1.1]">
                Cabinetry Made for <span className="italic text-primary">Your Kitchen</span>
              </h2>
              <p className="mt-6 text-base text-muted-foreground leading-relaxed">
                Whether you need fully custom cabinets, a complete renovation, or a refacing that transforms the look without the cost of replacement, every project starts with your space, your style, and your storage needs.
              </p>
              <Link
                to="/contact"
                data-testid="kitchens-consultation-button"
                className="mt-10 inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 hover:bg-accent hover:text-accent-foreground group"
              >
                Get a Free Consultation
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.15}>
              <ul className="divide-y divide-border border-y border-border">
                {FEATURES.map((f, i) => (
                  <li key={f} data-testid={`kitchens-feature-${i + 1}`} className="flex items-start gap-4 py-5">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="text-sm md:text-base text-foreground/85 leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-surface border-y border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>02 / Kitchen Inspiration</Overline>
            <h2 className="mt-5 mb-14 font-heading text-4xl md:text-5xl tracking-tight">
              Recent Kitchen <span className="italic text-primary">Styles</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {kitchenProjects.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div data-testid={`kitchens-gallery-${i + 1}`} className="group relative overflow-hidden aspect-[4/5]">
                  <img
                    src={p.img}
                    alt={p.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <h3 className="absolute bottom-5 left-5 font-heading text-2xl text-white tracking-tight">{p.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Let's Design Your"
        italic="Dream Kitchen"
        text="From custom cabinets to a full kitchen renovation, tell us about your space and we'll design a kitchen that works beautifully for your lifestyle."
      />
    </main>
  );
}
